import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateRecurringSchedule,
  useUpdateRecurringSchedule,
  useDeleteRecurringSchedule,
} from '@/hooks/useRecurringSchedules';
import type { RecurringInterval, RecurringJobSchedule } from '@jobos/shared';
import { todayLocalISO } from '@/lib/format';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceJobId: string;
  existing?: RecurringJobSchedule | null;
}


export function RecurringScheduleModal({ open, onOpenChange, sourceJobId, existing }: Props) {
  const [intervalValue, setIntervalValue] = useState<RecurringInterval>('monthly');
  const [startDate, setStartDate] = useState<string>(todayLocalISO());

  useEffect(() => {
    if (existing) {
      setIntervalValue(existing.interval);
      setStartDate(existing.startDate);
    } else {
      setIntervalValue('monthly');
      setStartDate(todayLocalISO());
    }
  }, [existing, open]);

  const createMut = useCreateRecurringSchedule();
  const updateMut = useUpdateRecurringSchedule();
  const deleteMut = useDeleteRecurringSchedule();

  const isPending = createMut.isPending || updateMut.isPending || deleteMut.isPending;

  const handleSave = async () => {
    if (existing) {
      await updateMut.mutateAsync({
        id: existing.id,
        update: { interval: intervalValue, startDate },
      });
    } else {
      await createMut.mutateAsync({ sourceJobId, interval: intervalValue, startDate });
    }
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (!existing) return;
    if (!confirm('Remove this recurring reminder?')) return;
    await deleteMut.mutateAsync(existing.id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing ? 'Manage recurring reminder' : 'Set up recurring reminder'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label
              htmlFor="interval"
              className="block text-sm font-medium text-gray-700"
            >
              Repeat every
            </label>
            <Select
              value={intervalValue}
              onValueChange={(v) => setIntervalValue(v as RecurringInterval)}
            >
              <SelectTrigger id="interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Month</SelectItem>
                <SelectItem value="quarterly">Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="startDate"
              className="block text-sm font-medium text-gray-700"
            >
              Start date
            </label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <p className="text-xs text-gray-500">First reminder fires on this date.</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {existing && (
            <Button variant="destructive" onClick={handleDelete} disabled={isPending}>
              Remove
            </Button>
          )}
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {existing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
